import React from "react";

import { Card, Notification, RadioButton } from "@stellar/design-system";

import { DisbursementVerificationField, VerificationFieldMap } from "@/types";

import "./styles.scss";

interface VerificationOption {
  value: string;
  label: string;
  subtitle: string;
}

const VERIFICATION_OPTION_META: Record<string, Omit<VerificationOption, "value">> = {
  DATE_OF_BIRTH: {
    label: "Date of birth, full",
    subtitle: "YYYY-MM-DD",
  },
  YEAR_MONTH: {
    label: "Date of birth, year and month",
    subtitle: "YYYY-MM",
  },
  NATIONAL_ID_NUMBER: {
    label: "National ID number",
    subtitle: "Government-issued ID number",
  },
  PIN: {
    label: "Custom code",
    subtitle: "4-8 character alphanumeric code. Case sensitive.",
  },
};

const CUSTOM_CODE_VALUE: DisbursementVerificationField = "PIN";

interface DisbursementVerificationProps {
  value: string;
  options: string[];
  onChange: (val: string) => void;
}

export const DisbursementVerification: React.FC<DisbursementVerificationProps> = ({
  value,
  options,
  onChange,
}: DisbursementVerificationProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(event.target.value);
  };

  const getOptionMeta = (optionValue: string): Omit<VerificationOption, "value"> => {
    return (
      VERIFICATION_OPTION_META[optionValue] ?? {
        label: VerificationFieldMap[optionValue] || optionValue,
        subtitle: "",
      }
    );
  };

  return (
    <Card>
      <p className="DisbursementVerification__note">
        <strong className="DisbursementVerification__note__title">
          Choose receiver verification method
        </strong>
        Choose how recipients will verify their identity. Once a receiver registers, this
        verification method is saved to their profile.{" "}
        <strong>
          Any future disbursements for registered users must match this specific field
        </strong>{" "}
        to be successful.
      </p>

      <div className="DisbursementVerification__options">
        {options.map((optionValue: string) => {
          const meta: Omit<VerificationOption, "value"> = getOptionMeta(optionValue);
          const isSelected: boolean = value === optionValue;

          return (
            <label
              key={optionValue}
              className={`DisbursementVerification__option${isSelected ? " DisbursementVerification__option--selected" : ""}`}
            >
              <RadioButton
                id={`verification-${optionValue}`}
                name="verification-field"
                fieldSize="sm"
                value={optionValue}
                checked={isSelected}
                onChange={handleChange}
                label=""
              />
              <div className="DisbursementVerification__option__text">
                <span className="DisbursementVerification__option__label">{meta.label}</span>
                {meta.subtitle && (
                  <span className="DisbursementVerification__option__subtitle">
                    {meta.subtitle}
                  </span>
                )}

                {isSelected && optionValue === CUSTOM_CODE_VALUE && (
                  <Notification variant="warning" title="">
                    <strong>Important:</strong> You are responsible for securely communicating the
                    code to each recipient. The system does not automatically distribute these
                    codes.
                  </Notification>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </Card>
  );
};
